/**
 * PortalLeadGate Component
 * Ensures portal users are registered as leads in Zoho CRM
 * Shows lead form modal if user is not yet a lead
 */
import { useEffect } from 'react';
import { useZohoLeadGate } from '@/hooks/useZohoLeadGate';
import { LeadCaptureForm } from '@/components/LeadCaptureForm';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, ShieldCheck } from 'lucide-react';

const API_URL = window.location.origin;

const PortalLeadGate = ({ 
  children, 
  userEmail, 
  userName,
  userPhone = '',
  userId = '',
  onVerified = () => {} 
}) => {
  const {
    isVerified,
    isLoading,
    showLeadForm,
    setShowLeadForm,
    zohoLeadId,
    onLeadFormComplete
  } = useZohoLeadGate(userEmail, userName);

  // Notify parent when verified
  useEffect(() => {
    if (isVerified && zohoLeadId) {
      onVerified(zohoLeadId);
    }
  }, [isVerified, zohoLeadId, onVerified]);

  // Handle form submission success - link student to Zoho lead
  const handleFormSuccess = async (response) => {
    const leadId = response?.lead_id || response?.zoho_lead_id;
    
    if (leadId && userId) {
      try {
        // Link the student to the Zoho lead
        await fetch(
          `${API_URL}/api/zoho/link-student?user_id=${encodeURIComponent(userId)}&zoho_lead_id=${encodeURIComponent(leadId)}`,
          { method: 'POST' }
        );
      } catch (error) {
        console.error('Error linking student to Zoho:', error);
      }
    }
    
    if (leadId) {
      onLeadFormComplete(leadId);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Verifying your profile...</p>
      </div>
    );
  }

  // Show lead form modal if not verified
  if (showLeadForm && !isVerified) {
    return (
      <>
        {/* Blurred background content */}
        <div className="filter blur-sm pointer-events-none">
          {children}
        </div>

        {/* Lead Form Modal */}
        <Dialog open={true} onOpenChange={() => {}}>
          <DialogContent 
            className="max-w-2xl max-h-[90vh] overflow-y-auto"
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <ShieldCheck className="w-6 h-6 text-primary" />
                Complete Your Profile
              </DialogTitle>
              <DialogDescription>
                Please provide a few more details to access the student portal. 
                This helps us connect you with the right counselor.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4">
              <LeadCaptureForm
                prefilledData={{
                  firstName: userName?.split(' ')[0] || '',
                  lastName: userName?.split(' ').slice(1).join(' ') || '',
                  email: userEmail || '',
                  phone: userPhone || ''
                }}
                onSuccess={handleFormSuccess}
                showHeader={false}
                variant="modal"
              />
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // User is verified, show children
  return children;
};

export default PortalLeadGate;
